import { useCallback, useRef, useState } from 'react';

import { cx } from '@libs/classnames';
import { useDebouncedInput } from '@libs/hooks/useDebouncedInput';
import { IconButton } from '@ui/components/IconButton';
import { Input } from '@ui/components/Input';
import { Loader } from '@ui/components/Loader';
import { DeleteIcon } from '@ui/icons/DeleteIcon';
import { SearchIcon } from '@ui/icons/SearchIcon';

import styles from './SearchInput.module.css';

export type Props = {
  className?: string;
  query?: string;
  onQueryChange: (query: string) => void;
  isLoading?: boolean;
  defaultValue?: string;
  placeholder?: string;
  collapsible?: boolean;
  onCollapseChange?: (isCollapsed: boolean) => void;
  disabled?: boolean;
};

export function SearchInput({
  className,
  query,
  onQueryChange,
  isLoading,
  defaultValue,
  placeholder,
  collapsible,
  onCollapseChange,
  disabled,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [localQuery, onLocalQueryChange] = useDebouncedInput(query || defaultValue, onQueryChange);
  const [expanded, setExpanded] = useState(false);

  const toggle = useCallback(() => {
    if (!collapsible) return;
    setExpanded(!expanded);
    onCollapseChange?.(expanded);
  }, [expanded, setExpanded, onCollapseChange, collapsible]);

  const onClear = useCallback(() => {
    onLocalQueryChange('');
    toggle();
    inputRef.current?.blur();
  }, [onLocalQueryChange, toggle]);

  const onClick = useCallback(() => {
    if (!collapsible || expanded) return;
    toggle();
    inputRef.current?.focus();
  }, [collapsible, toggle, expanded]);

  const onBlur = useCallback(() => {
    if (collapsible && expanded && !localQuery) toggle();
  }, [expanded, localQuery, toggle, collapsible]);

  const onFocus = useCallback(() => {
    if (collapsible && !expanded) toggle();
  }, [expanded, toggle, collapsible]);

  let searchIcon =
    localQuery || (expanded && collapsible) ? (
      <IconButton className={styles.clearIcon} onClick={onClear} size="28" preset="transparent">
        <DeleteIcon />
      </IconButton>
    ) : (
      <SearchIcon className={styles.searchIcon} />
    );

  if (isLoading) {
    searchIcon = <Loader className={styles.loader} />;
  }

  return (
    <Input
      inputRef={inputRef}
      className={cx(styles.searchInput, className, {
        [styles.collapsible]: collapsible,
        [styles.expanded]: expanded,
      })}
      flat
      onChange={onLocalQueryChange}
      value={localQuery}
      rightLabel={searchIcon}
      placeholder={collapsible ? '' : placeholder}
      onEscape={onClear}
      onClick={onClick}
      onBlur={onBlur}
      onFocus={onFocus}
      disabled={disabled}
    />
  );
}
